import { useEffect, useState, useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";
// Auth guard handled by AdminLayout
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useAdminData, type AdminUser } from "@/hooks/useAdminData";
import { useAdminTimeframe } from "@/hooks/useAdminTimeframe";
import { AdminTimeframeFilter } from "@/components/admin/AdminTimeframeFilter";
import { AdminCharts } from "@/components/admin/AdminCharts";
import { AdminMetricCard } from "@/components/admin/AdminMetricCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Users, Activity, Coins, TrendingUp, Search, Shield, Plus,
  RefreshCw, Loader2, UserPlus, Timer, Filter,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";


const AUTO_REFRESH_MS = 60_000;

export default function AdminDashboard() {
  const { isAdmin, loading: authLoading } = useAdminAuth();
  const {
    users, metrics, chartData, loading, chartLoading,
    fetchUsers, fetchMetrics, fetchChartData,
    addCredits, setMonthlyCredits, setRole,
  } = useAdminData();
  const { preset, customFrom, customTo, setPreset, setCustomRange, PRESETS } = useAdminTimeframe();

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [orgFilter, setOrgFilter] = useState<string>("all");
  const [creditFilter, setCreditFilter] = useState<string>("all");
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [creditAmount, setCreditAmount] = useState("");
  const [monthlyAmount, setMonthlyAmount] = useState("");
  const [dialogMode, setDialogMode] = useState<"credits" | "monthly" | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState(60);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  // Fetch data when timeframe or admin status changes
  const refreshAll = useCallback(() => {
    fetchUsers();
    fetchMetrics(preset, customFrom, customTo);
    fetchChartData(preset, customFrom, customTo);
    setSecondsUntilRefresh(60);
  }, [fetchUsers, fetchMetrics, fetchChartData, preset, customFrom, customTo]);

  useEffect(() => {
    if (isAdmin) refreshAll();
  }, [isAdmin, refreshAll]);

  // Auto-refresh every minute
  useEffect(() => {
    if (!isAdmin) return;
    const interval = setInterval(refreshAll, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [isAdmin, refreshAll]);

  // Countdown timer
  useEffect(() => {
    if (!isAdmin) return;
    const tick = setInterval(() => {
      setSecondsUntilRefresh((s) => (s <= 1 ? 60 : s - 1));
    }, 1000);
    return () => clearInterval(tick);
  }, [isAdmin]);

  // Derive unique org names for filter
  const orgNames = useMemo(() => Array.from(new Set(users.flatMap(u => u.organizations.map(o => o.org_name)))).sort(), [users]);

  const filteredUsers = useMemo(() => users.filter((u) => {
    const matchesSearch =
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.full_name?.toLowerCase().includes(search.toLowerCase());
    const matchesRole =
      roleFilter === "all" ||
      (roleFilter === "admin" && u.roles.includes("admin")) ||
      (roleFilter === "user" && !u.roles.includes("admin"));
    const matchesOrg =
      orgFilter === "all" ||
      (orgFilter === "none" && u.organizations.length === 0) ||
      u.organizations.some(o => o.org_name === orgFilter);
    const matchesCredit =
      creditFilter === "all" ||
      (creditFilter === "has_topup" && (u.credits?.topup_credits ?? 0) > 0) ||
      (creditFilter === "no_balance" && (u.credits ? (u.credits.monthly_credits - u.credits.monthly_used + u.credits.topup_credits) : 10) === 0) ||
      (creditFilter === "has_balance" && (u.credits ? (u.credits.monthly_credits - u.credits.monthly_used + u.credits.topup_credits) : 10) > 0);
    return matchesSearch && matchesRole && matchesOrg && matchesCredit;
  }), [users, search, roleFilter, orgFilter, creditFilter]);

  // Reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); }, [search, roleFilter, orgFilter, creditFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }


  const handleAddCredits = async () => {
    if (!selectedUser || !creditAmount) return;
    setActionLoading(true);
    try {
      await addCredits(selectedUser.user_id, parseInt(creditAmount));
      toast({ title: "Credits added", description: `Added ${creditAmount} credits to ${selectedUser.email}` });
      setCreditAmount("");
      setDialogMode(null);
      fetchUsers();
    } catch (e) {
      toast({ title: "Failed", description: String(e), variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetMonthly = async () => {
    if (!selectedUser || !monthlyAmount) return;
    setActionLoading(true);
    try {
      await setMonthlyCredits(selectedUser.user_id, parseInt(monthlyAmount));
      toast({ title: "Monthly credits updated", description: `Set to ${monthlyAmount} for ${selectedUser.email}` });
      setMonthlyAmount("");
      setDialogMode(null);
      fetchUsers();
    } catch (e) {
      toast({ title: "Failed", description: String(e), variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleAdmin = async (user: AdminUser) => {
    const isCurrentlyAdmin = user.roles.includes("admin");
    setActionLoading(true);
    try {
      await setRole(user.user_id, "admin", isCurrentlyAdmin);
      toast({
        title: isCurrentlyAdmin ? "Admin removed" : "Admin granted",
        description: `${user.email} ${isCurrentlyAdmin ? "is no longer admin" : "is now admin"}`,
      });
      fetchUsers();
    } catch (e) {
      toast({ title: "Failed", description: String(e), variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  const txBySource = metrics?.transactions_by_source || {};

  const metricItems = [
    { label: "Total Users", value: metrics?.total_users ?? "—", icon: Users },
    { label: "New Users", value: metrics?.new_users ?? "—", icon: UserPlus },
    { label: "Runs", value: metrics?.total_runs ?? "—", icon: Activity },
    { label: "Credits Allocated", value: metrics?.total_credits_allocated ?? "—", icon: Coins },
    { label: "Credits Used", value: metrics?.total_credits_used ?? "—", icon: TrendingUp },
    { label: "Top-up Balance", value: metrics?.total_topup_credits ?? "—", icon: Plus },
    { label: "Runs Consumed", value: txBySource.run_consumption ?? 0, icon: Activity },
    { label: "Top-ups Sold", value: txBySource.topup_purchase ?? 0, icon: Coins },
  ];

  return (
    <>
      <main className="container px-6 py-8 space-y-8 relative">
        {/* Timeframe filter */}
        <div className="animate-fade-in">
          <AdminTimeframeFilter
            preset={preset}
            customFrom={customFrom}
            customTo={customTo}
            presets={PRESETS}
            onPresetChange={setPreset}
            onCustomRangeChange={setCustomRange}
          />
        </div>

        {/* Metrics cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {metricItems.map((m, i) => (
            <AdminMetricCard
              key={m.label}
              label={m.label}
              value={m.value}
              icon={m.icon}
              index={i}
            />
          ))}
        </div>

        {/* Charts */}
        <AdminCharts data={chartData} loading={chartLoading} />

        {/* Users table */}
        <Card className="glass card-hover animate-fade-in" style={{ animationDelay: "300ms", animationFillMode: "both" }}>
          <CardHeader className="pb-4 border-b border-border/30">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">All Users ({filteredUsers.length})</CardTitle>
                <div className="relative w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by email or name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="admin">Admins</SelectItem>
                    <SelectItem value="user">Non-admins</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={orgFilter} onValueChange={setOrgFilter}>
                  <SelectTrigger className="w-[150px] h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Orgs</SelectItem>
                    <SelectItem value="none">No Org</SelectItem>
                    {orgNames.map(name => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={creditFilter} onValueChange={setCreditFilter}>
                  <SelectTrigger className="w-[150px] h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Credits</SelectItem>
                    <SelectItem value="has_topup">Has Top-ups</SelectItem>
                    <SelectItem value="has_balance">Has Balance</SelectItem>
                    <SelectItem value="no_balance">Zero Balance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Roles</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead className="text-right">Monthly</TableHead>
                      <TableHead className="text-right">Used</TableHead>
                      <TableHead className="text-right">Top-up</TableHead>
                      <TableHead>Top-up Expiry</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedUsers.map((user) => (
                      <TableRow key={user.user_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{user.full_name || "—"}</p>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {user.roles.length > 0 ? user.roles.map((r) => (
                              <Badge key={r} variant={r === "admin" ? "default" : "secondary"} className="text-xs">{r}</Badge>
                            )) : <span className="text-xs text-muted-foreground">user</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.organizations.length > 0 ? (
                            <div className="text-xs">
                              {user.organizations.map((o) => (
                                <div key={o.org_name}>{o.org_name} <span className="text-muted-foreground">({o.org_role})</span></div>
                              ))}
                            </div>
                          ) : <span className="text-xs text-muted-foreground">None</span>}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">{user.credits?.monthly_credits ?? 10}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{user.credits?.monthly_used ?? 0}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{user.credits?.topup_credits ?? 0}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {user.credits?.topup_expires_at
                            ? new Date(user.credits.topup_expires_at).toLocaleDateString()
                            : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(user.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(user); setDialogMode("credits"); }} title="Add top-up credits">
                              <Plus className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(user); setMonthlyAmount(String(user.credits?.monthly_credits ?? 10)); setDialogMode("monthly"); }} title="Set monthly credits">
                              <Coins className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleToggleAdmin(user)} title={user.roles.includes("admin") ? "Remove admin" : "Make admin"} disabled={actionLoading}>
                              <Shield className={`w-3.5 h-3.5 ${user.roles.includes("admin") ? "text-primary" : "text-muted-foreground"}`} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No users found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredUsers.length)} of {filteredUsers.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Previous</Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                    .reduce<(number | "ellipsis")[]>((acc, p, idx, arr) => {
                      if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("ellipsis");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === "ellipsis" ? (
                        <span key={`e${idx}`} className="px-2 text-xs text-muted-foreground">…</span>
                      ) : (
                        <Button key={item} variant={item === currentPage ? "default" : "outline"} size="sm" className="w-8 h-8 p-0 text-xs" onClick={() => setCurrentPage(item)}>
                          {item}
                        </Button>
                      )
                    )}
                  <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Refresh controls */}
      <div className="fixed bottom-4 right-4 flex items-center gap-2 z-50">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground px-3 py-1.5 rounded-full bg-card/80 border border-border/50 backdrop-blur">
          <Timer className="w-3.5 h-3.5 text-primary animate-pulse-slow" />
          <span>{secondsUntilRefresh}s</span>
        </div>
        <Button variant="outline" size="sm" onClick={refreshAll} className="gap-2 hover:border-primary/40 transition-colors bg-card/80 backdrop-blur">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Add Credits Dialog */}
      <Dialog open={dialogMode === "credits"} onOpenChange={() => setDialogMode(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Top-up Credits</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Adding credits to <span className="font-medium text-foreground">{selectedUser?.email}</span>.
            Credits expire 1 year from grant date.
          </p>
          <Input type="number" min="1" placeholder="Number of credits" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogMode(null)}>Cancel</Button>
            <Button onClick={handleAddCredits} disabled={actionLoading || !creditAmount}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Credits"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Monthly Credits Dialog */}
      <Dialog open={dialogMode === "monthly"} onOpenChange={() => setDialogMode(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Set Monthly Credits</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Setting monthly credit allocation for <span className="font-medium text-foreground">{selectedUser?.email}</span>. This resets their monthly usage.
          </p>
          <Input type="number" min="0" placeholder="Monthly credits" value={monthlyAmount} onChange={(e) => setMonthlyAmount(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogMode(null)}>Cancel</Button>
            <Button onClick={handleSetMonthly} disabled={actionLoading || !monthlyAmount}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>

  );
}
